import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { PraxoraP4Panel } from '@/components/shared/PraxoraP4Panel';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/context/AppContext';
import { adminApi, REPORT_STATUS_LABEL, REPORT_STATUS_TONE, FIT_VERDICT_LABEL, GAP_STATUS_TONE } from '@/lib/adminApi';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Save, RefreshCw, CheckCircle2, XCircle, Send,
  FileDown, FileText, History, Sparkles, ShieldAlert, ShieldCheck,
  Plus, Minus, GitCompare, Bot, User as UserIcon, Archive, AlertTriangle,
  ListChecks,
} from 'lucide-react';

const StatusPill = ({ status, t }) => {
  const tone = REPORT_STATUS_TONE[status] || REPORT_STATUS_TONE.draft_ai_generated;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${tone.bg} ${tone.text} ${tone.border} border`}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} />
      {t(`reports.review.statusLabel.${status}`, { defaultValue: REPORT_STATUS_LABEL[status] || status })}
    </span>
  );
};

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const EMPTY_CONTENT = {
  summary: '', strengths: [], weaknesses: [], risks: [], behaviors: [],
  suitability: { score: 0, verdict: 'borderline', rationale: '' },
  recommendations: [], training_suggestions: [], consultant_notes: '',
};

// ----- list editor primitive -----
// Founder 2026-08 (report-editor readability): narrative fields auto-grow to
// fit content so reviewers read top-to-bottom on the page scroll — no nested
// textarea scrollbars. Presentation-only; editing behavior unchanged.
const AutoGrowTextarea = ({ value, className = '', minHeight = 88, ...props }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight + 2, minHeight)}px`;
  }, [value, minHeight]);
  return (
    <Textarea
      ref={ref}
      value={value}
      className={`resize-none overflow-hidden text-[15px] md:text-[15px] leading-relaxed ${className}`}
      style={{ minHeight: `${minHeight}px` }}
      {...props}
    />
  );
};

const ListEditor = ({ label, items, onChange, testid, color = 'primary', t }) => (
  <div className="space-y-2" data-testid={testid}>
    <div className="flex items-center justify-between">
      <Label className={`text-[13px] uppercase tracking-wider font-semibold text-${color}`}>{label}</Label>
      <Button size="sm" variant="ghost" onClick={() => onChange([...(items || []), ''])} className="h-7 gap-1 text-sm">
        <Plus className="h-3.5 w-3.5" /> {t('reports.review.add')}
      </Button>
    </div>
    <div className="space-y-2">
      {(items || []).map((it, i) => (
        <div key={i} className="flex gap-2 items-start">
          <AutoGrowTextarea
            value={it}
            onChange={(e) => {
              const next = [...items];
              next[i] = e.target.value;
              onChange(next);
            }}
            className="flex-1"
            placeholder="…"
          />
          <Button size="sm" variant="ghost" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive">
            <Minus className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {(!items || items.length === 0) && (
        <p className="text-sm text-muted-foreground italic">{t('reports.review.empty')}</p>
      )}
    </div>
  </div>
);

// ----- diff helper (line-level set diff for list fields) -----
const diffLists = (oldList = [], newList = []) => {
  const oldSet = new Set(oldList);
  const newSet = new Set(newList);
  return {
    added: newList.filter((x) => !oldSet.has(x)),
    removed: oldList.filter((x) => !newSet.has(x)),
  };
};

// ----- compare panel -----
const ComparePanel = ({ versions, t }) => {
  const [aIdx, setAIdx] = useState(0);
  const [bIdx, setBIdx] = useState(versions.length - 1);
  const a = versions[aIdx]?.content || EMPTY_CONTENT;
  const b = versions[bIdx]?.content || EMPTY_CONTENT;

  const fields = ['summary', 'strengths', 'weaknesses', 'risks', 'behaviors',
                  'recommendations', 'training_suggestions', 'consultant_notes'];

  return (
    <Card className="p-6 shadow-card" data-testid="compare-panel">
      <div className="flex items-center gap-2 mb-4">
        <GitCompare className="h-4 w-4 text-primary" />
        <h3 className="font-display font-bold">{t('reports.review.compareVersions')}</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Select value={String(aIdx)} onValueChange={(v) => setAIdx(parseInt(v, 10))}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {versions.map((v, i) => (
              <SelectItem key={v.id} value={String(i)}>v{v.version_number} · {v.author_type === 'ai' ? t('reports.review.authorAi') : t('reports.review.authorHm')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(bIdx)} onValueChange={(v) => setBIdx(parseInt(v, 10))}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {versions.map((v, i) => (
              <SelectItem key={v.id} value={String(i)}>v{v.version_number} · {v.author_type === 'ai' ? t('reports.review.authorAi') : t('reports.review.authorHm')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-4 text-sm">
        {/* Dimensional narrative diff (advisor edits of AI narrative) */}
        {(() => {
          const na = a.p4?.narrative || {}; const nb = b.p4?.narrative || {};
          const keys = Array.from(new Set([...Object.keys(na), ...Object.keys(nb)]));
          return keys.map((k) => {
            const va = na[k]; const vb = nb[k];
            if (Array.isArray(va) || Array.isArray(vb)) {
              const { added, removed } = diffLists(va || [], vb || []);
              if (!added.length && !removed.length) return null;
              return (
                <div key={`p4-${k}`}>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{k}</p>
                  {removed.map((x, i) => <p key={`r${i}`} className="text-destructive line-through text-xs">− {x}</p>)}
                  {added.map((x, i) => <p key={`a${i}`} className="text-success text-xs">+ {x}</p>)}
                </div>
              );
            }
            if (typeof va === 'string' || typeof vb === 'string') {
              if ((va || '') === (vb || '')) return null;
              return (
                <div key={`p4-${k}`}>
                  <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{k}</p>
                  <p className="text-destructive line-through text-xs">{va || t('reports.review.empty2')}</p>
                  <p className="text-success text-xs">{vb || t('reports.review.empty2')}</p>
                </div>
              );
            }
            return null;
          });
        })()}
        {fields.map((f) => {
          const va = a[f]; const vb = b[f];
          if (Array.isArray(va)) {
            const { added, removed } = diffLists(va, vb);
            if (added.length === 0 && removed.length === 0) return null;
            return (
              <div key={f}>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{t(`reports.review.versionField.${f}`, { defaultValue: f })}</p>
                {removed.map((x, i) => <p key={`r${i}`} className="text-destructive line-through text-xs">− {x}</p>)}
                {added.map((x, i) => <p key={`a${i}`} className="text-success text-xs">+ {x}</p>)}
              </div>
            );
          }
          if (typeof va === 'string' && va !== vb) {
            return (
              <div key={f}>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{t(`reports.review.versionField.${f}`, { defaultValue: f })}</p>
                <p className="text-destructive line-through text-xs">{va || t('reports.review.empty2')}</p>
                <p className="text-success text-xs">{vb || t('reports.review.empty2')}</p>
              </div>
            );
          }
          if (f === 'suitability' && JSON.stringify(va) !== JSON.stringify(vb)) {
            return (
              <div key={f}>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">{t('reports.review.versionField.suitability')}</p>
                <p className="text-destructive line-through text-xs">{va?.score} / {t(`reports.review.verdictLabel.${va?.verdict}`, { defaultValue: va?.verdict })}</p>
                <p className="text-success text-xs">{vb?.score} / {t(`reports.review.verdictLabel.${vb?.verdict}`, { defaultValue: vb?.verdict })}</p>
              </div>
            );
          }
          return null;
        })}
      </div>
    </Card>
  );
};

export default function AdminReportReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useApp();
  const isAdmin = user?.role === 'hm_admin';
  const isReviewer = user?.role === 'hm_reviewer' || isAdmin;
  const isConsultant = user?.role === 'hm_advisor' || isAdmin;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [activeVersionIdx, setActiveVersionIdx] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenHint, setRegenHint] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmails, setSendEmails] = useState('');
  const [sendIncludeEval, setSendIncludeEval] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendLog, setSendLog] = useState([]);
  // Sesión F (ADD 2) — mandatory human-review confirmation before release.
  const [humanReviewConfirmed, setHumanReviewConfirmed] = useState(false);
  // R1 — optional HM Admin review + archive request + discard.
  const [reviewDialog, setReviewDialog] = useState(null); // 'return' | 'complete'
  const [reviewComments, setReviewComments] = useState('');
  const [archiveReqOpen, setArchiveReqOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('duplicate');
  const [archiveReasonText, setArchiveReasonText] = useState('');
  const [discardOpen, setDiscardOpen] = useState(false);

  const reload = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const j = await adminApi.getReport(id);
      setData(j);
      // load the current version's content into the editor by default
      const idx = j.versions.findIndex((v) => v.id === j.report.current_version_id);
      const useIdx = idx >= 0 ? idx : j.versions.length - 1;
      setActiveVersionIdx(useIdx);
      setContent(j.versions[useIdx]?.content || EMPTY_CONTENT);
    } catch (e) {
      if (!silent) setError(formatApiError(e));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Async Report Pipeline P2 (Batch 3) — while the AI draft is generating,
  // poll every 5s and swap to the real report automatically when ready.
  const genStatus = data?.report?.status;
  useEffect(() => {
    if (genStatus !== 'processing') return undefined;
    const h = setInterval(() => { reload(true); }, 5000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genStatus, id]);

  const doRetryGeneration = async () => {
    setBusy(true);
    try {
      await adminApi.retryGeneration(id);
      toast.success(t('reports.generation.retryQueued', { defaultValue: 'Report generation restarted' }));
      await reload(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const report = data?.report;
  const versions = data?.versions || [];
  const candidate = data?.candidate;
  const company = data?.company;
  const fit = report?.fit;
  const profile = data?.role_profile;
  const auditLog = data?.audit_log || [];

  const isTerminal = report && ['released_to_client', 'rejected', 'archived', 'discarded'].includes(report.status);
  const isPendingAdminReview = report?.status === 'pending_admin_review';
  const adminReview = report?.admin_review || null;
  const canStart = report?.status === 'draft_ai_generated' && isConsultant;
  const canSave = !isTerminal && !isPendingAdminReview && isConsultant;
  const canRegen = !isTerminal && !isPendingAdminReview && isConsultant;
  // R1 — HM Advisor is the official final approver (admin retains override).
  const canApprove = ['under_review', 'edited_by_hm'].includes(report?.status) && isConsultant;
  const canReject = !isTerminal && isAdmin;
  // Sesión F (ADD 2) — release allowed for hm_admin AND hm_advisor; the
  // mandatory checkbox below additionally gates the button.
  const canRelease = report?.status === 'approved' && (isAdmin || user?.role === 'hm_advisor');
  const canArchive = !['archived', 'discarded'].includes(report?.status) && isAdmin;
  // R1 — optional HM Admin quality review, archive request, discard.
  // R1 polish — only the Advisor requests the optional QA review (admin IS the reviewer).
  const canRequestAdminReview = ['under_review', 'edited_by_hm'].includes(report?.status) && user?.role === 'hm_advisor';
  const canCancelAdminReview = isPendingAdminReview && adminReview?.state === 'requested' && isConsultant;
  const canRequestArchive = user?.role === 'hm_advisor' && !['archived', 'discarded'].includes(report?.status) && !isPendingAdminReview && !report?.archive_request;
  const canDiscard = ['draft_ai_generated', 'under_review', 'edited_by_hm'].includes(report?.status) && isConsultant;
  const reviewedVersionNo = adminReview?.reviewed_version_id
    ? versions.find((v) => v.id === adminReview.reviewed_version_id)?.version_number
    : null;
  const reviewStale = !!(adminReview && ['returned', 'completed'].includes(adminReview.state)
    && adminReview.reviewed_version_id && report?.current_version_id !== adminReview.reviewed_version_id);

  const wrapAction = (fn, msg) => async () => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      await reload();
    } catch (e) {
      toast.error(t('toasts.actionFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const onSave = wrapAction(() => adminApi.saveEdit(
    id, content,
    ['praxora_p4', 'dimensional_profile'].includes(content.report_mode) ? content.p4?.narrative : undefined,
  ), t('reports.review.newVersionSaved'));
  const onStart = wrapAction(() => adminApi.startReview(id), t('reports.review.reviewStarted'));
  const onApprove = wrapAction(() => adminApi.approve(id), t('reports.review.reportApproved'));
  const onRelease = async () => {
    if (!humanReviewConfirmed) {
      toast.error(t('reports.action.humanReviewRequiredToast'));
      return;
    }
    setBusy(true);
    try {
      await adminApi.release(id, { human_review_confirmed: true });
      toast.success(t('reports.review.reportReleased'));
      setHumanReviewConfirmed(false);
      await reload();
    } catch (e) {
      toast.error(t('toasts.actionFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };
  const onArchive = wrapAction(() => adminApi.archive(id), t('reports.review.reportArchived'));
  // R1 — optional HM Admin review handlers.
  const onRequestAdminReview = wrapAction(() => adminApi.requestAdminReview(id), t('reports.review.adminReviewRequested'));
  const onCancelAdminReview = wrapAction(() => adminApi.cancelAdminReview(id), t('reports.review.adminReviewCancelled'));
  const onStartAdminReview = wrapAction(() => adminApi.startAdminReview(id), t('reports.review.adminReviewStartedToast'));
  const onReviewDecision = async () => {
    if (reviewDialog === 'return' && !reviewComments.trim()) {
      toast.error(t('reports.review.reviewCommentsRequired'));
      return;
    }
    setBusy(true);
    try {
      if (reviewDialog === 'return') await adminApi.returnWithComments(id, reviewComments.trim());
      else await adminApi.completeAdminReview(id, reviewComments.trim() || null);
      toast.success(reviewDialog === 'return' ? t('reports.review.reviewReturnedToast') : t('reports.review.reviewCompletedToast'));
      setReviewDialog(null); setReviewComments('');
      await reload();
    } catch (e) {
      toast.error(t('toasts.actionFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };
  const onRequestArchive = async () => {
    if (archiveReason === 'other' && !archiveReasonText.trim()) {
      toast.error(t('reports.review.archiveReasonRequired'));
      return;
    }
    setBusy(true);
    try {
      await adminApi.requestReportArchive(id, { reason_code: archiveReason, reason_text: archiveReasonText.trim() || null });
      toast.success(t('reports.review.archiveRequestedToast'));
      setArchiveReqOpen(false); setArchiveReasonText('');
      await reload();
    } catch (e) {
      toast.error(t('toasts.actionFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };
  const onDiscard = async () => {
    setBusy(true);
    try {
      await adminApi.discardReport(id);
      toast.success(t('reports.review.discardedToast'));
      setDiscardOpen(false);
      navigate('/admin/reports');
    } catch (e) {
      toast.error(t('toasts.actionFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };
  const onRegen = async () => {
    setBusy(true); setRegenOpen(false);
    try {
      // CTH-TMAP-086 — regeneration is asynchronous: the API only queues a
      // durable job; the processing panel polls until the new draft is ready.
      await adminApi.regenerate(id, regenHint || undefined);
      setRegenHint('');
      toast.success(t('reports.generation.regenQueued', { defaultValue: 'Regeneración iniciada — el informe se actualizará automáticamente al terminar.' }));
      await reload();
    } catch (e) {
      toast.error(t('toasts.regenerateFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };
  const onReject = async () => {
    if (!rejectReason.trim()) { toast.error(t('reports.review.reasonRequired')); return; }
    setBusy(true); setRejectOpen(false);
    try {
      await adminApi.reject(id, rejectReason);
      toast.success(t('toasts.reportRejected'));
      setRejectReason('');
      await reload();
    } catch (e) {
      toast.error(t('toasts.rejectFailed'), { description: formatApiError(e) });
    } finally { setBusy(false); }
  };

  const exportable = ['approved', 'released_to_client'].includes(report?.status);
  const canSend = report?.status && ['approved', 'released_to_client'].includes(report.status) && (isAdmin || user?.role === 'hm_advisor');

  const loadSendLog = async () => {
    try {
      const { data: rows } = await api.get(`/admin/reports/${id}/send-log`);
      setSendLog(rows || []);
    } catch { /* non-fatal */ }
  };

  const openSendDialog = async () => {
    const defaults = [company?.contact_email].filter(Boolean).join(', ');
    setSendEmails(defaults);
    setSendIncludeEval(false);
    setSendOpen(true);
    await loadSendLog();
  };

  const onSend = async () => {
    const recipients = sendEmails
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (recipients.length === 0 && !sendIncludeEval) {
      toast.error(t('toasts.addAtLeastOneRecipient'));
      return;
    }
    setSending(true);
    try {
      const { data: res } = await api.post(`/admin/reports/${id}/send`, {
        recipients,
        include_evaluated: sendIncludeEval,
      });
      toast.success(t('toasts.reportEmailed'), { description: `Delivered to ${res.recipients.length} recipient(s).` });
      setSendOpen(false);
      setSendEmails('');
      setSendIncludeEval(false);
      await loadSendLog();
    } catch (e) {
      toast.error(t('toasts.sendFailed'), { description: formatApiError(e) });
    } finally {
      setSending(false);
    }
  };

  const triggerDownload = async (fmt) => {
    try {
      const res = await api.get(`/admin/reports/${id}/export/${fmt}`, { responseType: 'blob' });
      const disp = res.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(disp);
      const filename = m ? m[1] : `CTH_Report.${fmt}`;
      // Preserve the server MIME type (a typeless Blob breaks DOCX saves in
      // Safari-class browsers) and defer revoke so the save can complete.
      const blob = res.data instanceof Blob
        ? res.data
        : new Blob([res.data], { type: res.headers['content-type'] || '' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.rel = 'noopener';
      document.body.appendChild(a); a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 4000);
      toast.success(t('reports.review.downloaded', { fmt: fmt.toUpperCase() }));
    } catch (e) {
      toast.error(t('toasts.exportFailed'), { description: formatApiError(e) });
    }
  };
  const exportPdf = () => triggerDownload('pdf');
  const exportWord = () => triggerDownload('docx');

  const activeVersion = versions[activeVersionIdx];
  // TC-Closeout FIX 8 — a system-authored draft with no content must never
  // look like a finished report; FIX 12 — TC reports expose Idoneidad status.
  const isTcReport = data?.assignment?.suite_code === 'talentcompetency';
  const isTtReport = data?.assignment?.suite_code === 'talenttrain';
  const isBlankDraft = report?.status === 'draft_ai_generated'
    && activeVersion?.author_type === 'system'
    && !(content.summary || '').trim()
    && !(content.strengths || []).length
    && !(content.recommendations || []).length;
  const compName = (g) => t(`reports.competencies.${String(g.competency_code || g.competency_name || '').toLowerCase()}`,
    { defaultValue: g.competency_name });

  if (loading) {
    return (
      <>
        <Topbar title={t('reports.review.reportLabel')} subtitle={t('reports.review.loading')} />
        <div className="p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('reports.review.loadingReport')}
        </div>
      </>
    );
  }
  if (error || !report) {
    return (
      <>
        <Topbar title={t('reports.review.reportLabel')} subtitle={t('reports.review.errorLabel')} />
        <div className="p-12 text-center">
          <p className="text-sm text-destructive">{error || t('reports.review.reportNotFound')}</p>
          <Button onClick={() => navigate('/admin/reports')} className="mt-6">{t('reports.review.backToReports')}</Button>
        </div>
      </>
    );
  }

  // Async Report Pipeline P2 (Batch 3) — dedicated states while the AI draft
  // is queued / processing / failed. Clarity over complexity: one card, one
  // message, one action.
  if (report.status === 'processing' || report.status === 'generation_failed') {
    const gen = report.generation || {};
    const failed = report.status === 'generation_failed';
    const isQueued = !failed && gen.state === 'queued';
    const failureMsg = (i18n.language || '').startsWith('en')
      ? (gen.failure_message_en || gen.failure_message_es)
      : (gen.failure_message_es || gen.failure_message_en);
    return (
      <>
        <Topbar
          title={candidate?.name || t('reports.review.reportLabel')}
          subtitle={failed ? t('reports.generation.failedTitle') : t('reports.generation.processingTitle')}
        />
        <div className="p-6 lg:p-8" data-testid="report-generation-panel">
          <Card className="p-10 shadow-card max-w-2xl mx-auto text-center space-y-6">
            {failed
              ? <AlertTriangle className="h-12 w-12 text-destructive mx-auto" data-testid="generation-failed-icon" />
              : <Loader2 className="h-12 w-12 text-info mx-auto animate-spin" data-testid="generation-processing-icon" />}
            <div className="space-y-2">
              <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="generation-panel-title">
                {failed ? t('reports.generation.failedTitle') : t('reports.generation.processingTitle')}
              </h2>
              <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed" data-testid="generation-panel-message">
                {failed
                  ? (failureMsg || t('reports.generation.failedBody'))
                  : t('reports.generation.processingBody')}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
              <Badge
                variant="outline"
                className={failed
                  ? 'bg-destructive/10 text-destructive border-destructive/30'
                  : 'bg-info/10 text-info border-info/30'}
                data-testid="generation-state-chip"
              >
                {failed
                  ? t('reports.generation.failedChip')
                  : isQueued
                    ? t('reports.generation.queuedChip')
                    : t('reports.generation.processingChip')}
              </Badge>
              {!failed && (gen.attempts || 0) > 1 && (
                <span data-testid="generation-attempt-note">{t('reports.generation.attempt', { n: gen.attempts, max: 3 })}</span>
              )}
              {failed && gen.failed_at && (
                <span data-testid="generation-failed-at">{t('reports.generation.failedAt', { date: fmt(gen.failed_at) })}</span>
              )}
              <span className="text-muted-foreground/70">{candidate?.name || '—'} · {company?.name || '—'}</span>
            </div>
            {failed && gen.can_retry && (
              <p className="text-xs text-muted-foreground" data-testid="generation-retry-hint">
                {t('reports.generation.retryHint')}
              </p>
            )}
            <div className="flex items-center justify-center gap-3">
              <Button variant="outline" onClick={() => navigate('/admin/reports')} className="gap-1.5" data-testid="generation-panel-back">
                <ArrowLeft className="h-4 w-4" /> {t('reports.review.backToReports')}
              </Button>
              {failed && gen.can_retry && (
                <Button onClick={doRetryGeneration} disabled={busy} className="gap-1.5" data-testid="generation-retry-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {t('reports.generation.retryBtn')}
                </Button>
              )}
            </div>
            {!failed && (
              <p className="text-xs text-muted-foreground/70" data-testid="generation-auto-note">
                {t('reports.generation.autoUpdates')}
              </p>
            )}
            {failed && !gen.can_retry && (
              <p className="text-xs text-muted-foreground/70" data-testid="generation-no-retry-note">
                {t('reports.generation.contactAdmin')}
              </p>
            )}
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar title={candidate?.name || t('reports.review.reportLabel')} subtitle={report.assignment_id ? t('reports.review.evaluationReport') : ''} />
      <div className="p-6 lg:p-8 space-y-6 pb-20" data-testid="admin-report-review">
        {/* Header bar */}
        <Card className="p-5 shadow-card">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-start gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin/reports')} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> {t('reports.review.reportsBtn')}</Button>
              <div>
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{data?.candidate_kind === 'external_candidate' ? t('reports.review.externalCandidateCompany') : t('reports.review.candidateCompany')}</p>
                <h2 className="font-display text-xl font-bold tracking-tight mt-0.5">{candidate?.name || '—'} · <span className="text-muted-foreground font-medium">{company?.name || '—'}</span></h2>
                {profile && (
                  <p className="text-xs text-muted-foreground mt-1">{t('reports.review.roleLabel')} <span className="font-medium text-foreground">{profile.position_title}</span>{profile.department ? ` · ${profile.department}` : ''}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill status={report.status} t={t} />
              <Badge variant="outline" className="text-xs">v{activeVersion?.version_number} {activeVersion?.author_type === 'ai' ? <Bot className="ml-1 h-3 w-3" /> : <UserIcon className="ml-1 h-3 w-3" />}</Badge>
            </div>
          </div>

          {/* R1 — pending HM Admin review: lock banner + admin decision bar */}
          {isPendingAdminReview && (
            <div className="mt-4 rounded-md border border-accent/40 bg-accent/5 px-4 py-3 space-y-2" data-testid="pending-admin-review-banner">
              <div className="flex items-start gap-2">
                <ShieldAlert className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                <div className="text-xs leading-relaxed">
                  <p className="font-semibold">{t('reports.review.pendingReviewBanner')}</p>
                  <p className="text-muted-foreground">
                    {t('reports.review.pendingReviewRequestedBy', { name: adminReview?.requested_by_name || '—', date: fmt(adminReview?.requested_at) })}
                    {reviewedVersionNo ? ` · ${t('reports.review.reviewedVersion', { n: reviewedVersionNo })}` : ''}
                  </p>
                  <p className="text-muted-foreground">{t('reports.review.pendingReviewNotify', { defaultValue: "You'll be notified when HM Admin completes the review." })}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pl-6">
                {canCancelAdminReview && (
                  <Button size="sm" variant="outline" onClick={onCancelAdminReview} disabled={busy} data-testid="btn-cancel-admin-review">
                    {t('reports.review.cancelReviewRequest')}
                  </Button>
                )}
                {isAdmin && adminReview?.state === 'requested' && (
                  <Button size="sm" onClick={onStartAdminReview} disabled={busy} data-testid="btn-start-admin-review" className="bg-gradient-primary hover:opacity-90">
                    {t('reports.review.startAdminReview')}
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => { setReviewComments(''); setReviewDialog('return'); }} disabled={busy} data-testid="btn-return-with-comments" className="border-warning/40 text-warning hover:bg-warning/5">
                      {t('reports.review.returnWithComments')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setReviewComments(''); setReviewDialog('complete'); }} disabled={busy} data-testid="btn-complete-review" className="border-success/40 text-success hover:bg-success/5">
                      {t('reports.review.completeReview')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* R1 — persistent HM Admin review outcome panel (visible to the Advisor) */}
          {adminReview && ['returned', 'completed'].includes(adminReview.state) && (
            <div
              className={`mt-4 rounded-md border px-4 py-3 space-y-1.5 ${reviewStale ? 'border-border bg-muted/40' : adminReview.state === 'returned' ? 'border-warning/40 bg-warning/5' : 'border-success/40 bg-success/5'}`}
              data-testid="admin-review-panel"
            >
              <div className="flex items-center gap-2 text-xs font-semibold">
                {adminReview.state === 'returned'
                  ? <><ShieldAlert className="h-4 w-4 text-warning" /> {t('reports.review.adminReviewPanelReturned')}</>
                  : <><ShieldCheck className="h-4 w-4 text-success" /> {t('reports.review.adminReviewPanelCompleted')}</>}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="admin-review-meta">
                {adminReview.decided_by_name || '—'} · {fmt(adminReview.decided_at)}
                {reviewedVersionNo ? ` · ${t('reports.review.reviewedVersion', { n: reviewedVersionNo })}` : ''}
              </p>
              {adminReview.comments && (
                <p className="text-xs leading-relaxed whitespace-pre-wrap border-l-2 border-border pl-2.5" data-testid="admin-review-comments">{adminReview.comments}</p>
              )}
              {reviewStale && (
                <p className="text-xs italic text-muted-foreground" data-testid="admin-review-stale-note">{t('reports.review.staleReviewNote')}</p>
              )}
            </div>
          )}

          {/* R1 — pending archive request indicator */}
          {report.archive_request && !['archived', 'discarded'].includes(report.status) && (
            <div className="mt-4 flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs" data-testid="archive-request-banner">
              <Archive className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
              <span>
                {t('reports.review.archiveRequestedBanner', {
                  name: report.archive_request.requested_by_name || '—',
                  reason: t(`reports.review.archiveReason.${report.archive_request.reason_code}`, { defaultValue: report.archive_request.reason_code }),
                })}
                {report.archive_request.reason_text ? ` — ${report.archive_request.reason_text}` : ''}
              </span>
            </div>
          )}

          {/* Sesión F (ADD 2) — mandatory human-review disclaimer before release */}
          {canRelease && (
            <div className="mt-4 -mb-1 flex items-start gap-2.5 rounded-md border border-warning/40 bg-warning/5 px-3 py-2.5" data-testid="release-disclaimer">
              <input
                id="human-review-confirm"
                type="checkbox"
                checked={humanReviewConfirmed}
                onChange={(e) => setHumanReviewConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-warning/60 text-warning focus:ring-warning"
                data-testid="human-review-checkbox"
              />
              <label htmlFor="human-review-confirm" className="text-xs leading-relaxed text-foreground/80 cursor-pointer">
                {t('reports.action.humanReviewLabel')}
              </label>
            </div>
          )}

          {/* Action buttons — UX U9: visible groups (primary / utilities / danger) */}
          <div className="mt-5 pt-5 border-t border-border space-y-3">
            <div className="flex flex-wrap items-center gap-2" data-testid="actions-primary">
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground w-20 shrink-0">{t('reports.review.groupPrimary', { defaultValue: 'Lifecycle' })}</span>
              {canStart && <Button data-testid="btn-start-review" onClick={onStart} disabled={busy} variant="outline" className="gap-1.5"><Sparkles className="h-4 w-4" /> {t('reports.review.startReview')}</Button>}
              <Button data-testid="btn-save" onClick={onSave} disabled={busy || !canSave} className="gap-1.5 bg-gradient-primary hover:opacity-90">
                <Save className="h-4 w-4" /> {t('reports.review.saveEdits')}
              </Button>
              <span className="inline-flex" title={!canApprove ? t('reports.review.hintApproveDisabled', { defaultValue: 'Available while the report is under review or edited by the advisor' }) : undefined}>
                <Button data-testid="btn-approve" onClick={onApprove} disabled={busy || !canApprove} variant="outline" className="gap-1.5 border-success/40 text-success hover:bg-success/5"><CheckCircle2 className="h-4 w-4" /> {t('reports.review.approve')}</Button>
              </span>
              {canRequestAdminReview && (
                <Button data-testid="btn-request-admin-review" onClick={onRequestAdminReview} disabled={busy} variant="outline" className="gap-1.5 border-accent/40 text-accent hover:bg-accent/5"><ShieldAlert className="h-4 w-4" /> {t('reports.review.requestAdminReview')}</Button>
              )}
              <span className="inline-flex" title={!canRelease ? t('reports.review.hintReleaseDisabled', { defaultValue: 'Available once the report is approved' }) : (!humanReviewConfirmed ? t('reports.review.hintConfirmHumanReview', { defaultValue: 'Confirm the human-review checkbox above to release' }) : undefined)}>
                <Button data-testid="btn-release" onClick={onRelease} disabled={busy || !canRelease || !humanReviewConfirmed} className="gap-1.5 bg-success hover:bg-success/90"><Send className="h-4 w-4" /> {t('reports.review.release')}</Button>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="actions-utilities">
              <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground w-20 shrink-0">{t('reports.review.groupUtilities', { defaultValue: 'Utilities' })}</span>
              <Button data-testid="btn-refresh-report" onClick={() => reload()} disabled={busy} variant="ghost" size="sm" className="gap-1.5 text-muted-foreground"><RefreshCw className="h-4 w-4" /> {t('reports.review.refresh', { defaultValue: 'Refresh' })}</Button>
              <Button data-testid="btn-regenerate" onClick={() => setRegenOpen(true)} disabled={busy || !canRegen} variant="outline" size="sm" className="gap-1.5"><RefreshCw className="h-4 w-4" /> {t('reports.review.regenerate')}</Button>
              <span className="inline-flex" title={!canSend ? t('reports.review.hintSendDisabled', { defaultValue: 'Available once the report is approved' }) : undefined}>
                <Button data-testid="btn-send-report" variant="outline" size="sm" onClick={openSendDialog} disabled={!canSend} className="gap-1.5 border-primary/40 text-primary hover:bg-primary/5"><Send className="h-4 w-4" /> {report?.status === 'released_to_client' ? t('reports.review.resendReport') : t('reports.review.sendReport')}</Button>
              </span>
              <span className="inline-flex" title={!exportable ? t('reports.review.hintExportDisabled', { defaultValue: 'Available once the report is approved' }) : undefined}>
                <Button data-testid="btn-export-pdf" variant="outline" size="sm" onClick={exportPdf} disabled={!exportable} className="gap-1.5"><FileDown className="h-4 w-4" /> {t('reports.review.exportPdf')}</Button>
              </span>
              <span className="inline-flex" title={!exportable ? t('reports.review.hintExportDisabled', { defaultValue: 'Available once the report is approved' }) : undefined}>
                <Button data-testid="btn-export-docx" variant="outline" size="sm" onClick={exportWord} disabled={!exportable} className="gap-1.5"><FileText className="h-4 w-4" /> {t('reports.review.exportDocx', { defaultValue: 'Word (DOCX)' })}</Button>
                {report?.suite_code_hint === 'talentcompetency' && (
                  <Button data-testid="btn-export-responses" variant="ghost" size="sm" onClick={() => triggerDownload('responses-pdf')} disabled={!exportable} className="gap-1.5 text-muted-foreground border-l border-border rounded-none pl-3 ml-1">
                    <ListChecks className="h-4 w-4" /> {t('reports.review.exportResponses', { defaultValue: 'Respuestas del cuestionario' })}
                  </Button>
                )}
              </span>
            </div>
            {(isAdmin || user?.role === 'hm_advisor') && (
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/20 bg-destructive/[0.03] px-2 py-2" data-testid="actions-danger">
                <span className="text-xs uppercase tracking-wider font-semibold text-destructive/70 w-20 shrink-0">{t('reports.review.groupDanger', { defaultValue: 'Danger zone' })}</span>
                {isAdmin && <Button data-testid="btn-reject" onClick={() => setRejectOpen(true)} disabled={busy || !canReject} variant="outline" size="sm" className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/5"><XCircle className="h-4 w-4" /> {t('reports.review.reject')}</Button>}
                {isAdmin && <Button data-testid="btn-archive" onClick={onArchive} disabled={busy || !canArchive} variant="outline" size="sm" className="gap-1.5 text-muted-foreground"><Archive className="h-4 w-4" /> {t('reports.review.archive')}</Button>}
                {user?.role === 'hm_advisor' && !['archived', 'discarded'].includes(report?.status) && (
                  <Button data-testid="btn-request-archive" onClick={() => setArchiveReqOpen(true)} disabled={busy || !canRequestArchive} variant="outline" size="sm" className="gap-1.5 text-muted-foreground"><Archive className="h-4 w-4" /> {t('reports.review.requestArchive')}</Button>
                )}
                {canDiscard && (
                  <Button data-testid="btn-discard" onClick={() => setDiscardOpen(true)} disabled={busy} variant="outline" size="sm" className="gap-1.5 text-muted-foreground hover:text-destructive"><XCircle className="h-4 w-4" /> {t('reports.review.discard')}</Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* CTH-TMAP-086 — explicit failed-regeneration state: the previous
            valid version is preserved; the consultant can retry. */}
        {report?.generation?.state === 'regenerate_failed' && (
          <Card className="p-5 shadow-card border-destructive/40 bg-destructive/5" data-testid="report-regen-failed-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-sm">{t('reports.generation.regenFailedTitle', { defaultValue: 'La regeneración con IA falló' })}</p>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="report-regen-failed-msg">
                  {(i18n.language || '').startsWith('es')
                    ? (report.generation.failure_message_es || 'La regeneración falló — se conservó la última versión válida. Puede reintentar con el botón Regenerar.')
                    : (report.generation.failure_message_en || 'The regeneration failed — the previous valid version was preserved. You can retry with the Regenerate button.')}
                </p>
              </div>
            </div>
          </Card>
        )}
        {/* TC-Closeout FIX 8 — explicit generating / blank-draft states.
            CTH-TMAP-086: regeneration is now async (processing panel), the
            legacy synchronous banner shows only while a request is in flight. */}
        {regenerating && (
          <Card className="p-5 shadow-card border-info/40 bg-info/5" data-testid="report-regenerating-banner">
            <div className="flex items-start gap-3">
              <Loader2 className="h-5 w-5 text-info animate-spin mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-sm">{t('reports.review.generatingTitle', { defaultValue: 'Generando informe…' })}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{t('reports.review.generatingBody', { defaultValue: 'La IA está redactando el borrador. Esto puede tardar hasta un minuto — no cierre esta página.' })}</p>
              </div>
            </div>
          </Card>
        )}
        {!regenerating && isBlankDraft && (
          <Card className="p-5 shadow-card border-warning/40 bg-warning/5" data-testid="report-blank-draft-banner">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-3 flex-1">
                <AlertTriangle className="h-5 w-5 text-warning mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-sm">{t('reports.review.tcBlankTitle', { defaultValue: 'Borrador inicial sin interpretación' })}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('reports.review.tcBlankBody', { defaultValue: 'Este informe aún no contiene interpretación profesional. Genere el borrador IA o complete las secciones manualmente.' })}</p>
                </div>
              </div>
              {canRegen && (
                <Button size="sm" onClick={() => setRegenOpen(true)} disabled={busy} className="gap-1.5 shrink-0" data-testid="blank-draft-generate-btn">
                  <Sparkles className="h-4 w-4" /> {t('reports.review.regenAiDraft')}
                </Button>
              )}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* LEFT: editor */}
          <div className="xl:col-span-2 space-y-4">
            <Tabs defaultValue="editor">
              <TabsList>
                <TabsTrigger value="editor" data-testid="tab-editor">{t('reports.review.editor')}</TabsTrigger>
                <TabsTrigger value="versions" data-testid="tab-versions">{t('reports.review.versions')} ({versions.length})</TabsTrigger>
                <TabsTrigger value="compare" data-testid="tab-compare">{t('reports.review.compare')}</TabsTrigger>
              </TabsList>

              <TabsContent value="editor" className="space-y-4 mt-4">
                {/* BUG 08 — Integrity scales card (Inconsistency + Impression).
                     Shown consistently across all reports; defaults to neutral
                     when no tagged items exist. Consultant-only. */}
                {isConsultant && data?.integrity_scales && (
                  <Card className="p-5 shadow-card border-amber-200/60 bg-amber-50/30" data-testid="integrity-scales-card">
                    <div className="flex items-start gap-3">
                      <div className="rounded-lg bg-amber-100/80 text-amber-700 p-2"><AlertTriangle className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-sm">{t('reports.review.integrityTitle', { defaultValue: 'Validity scales · consultant-only' })}</h4>
                        <p className="text-xs text-muted-foreground">{t('reports.review.integrityHint', { defaultValue: 'Internal indicators — never displayed to the evaluated person.' })}</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 mt-4">
                      {[
                        { key: 'inconsistency', label: t('reports.review.inconsistency', { defaultValue: 'Inconsistency' }), s: data.integrity_scales.inconsistency, hint: t('reports.review.inconsistencyHint', { defaultValue: '% of paired items answered in opposite directions' }), explain: t('reports.review.inconsistencyExplain', { defaultValue: 'Mide contradicciones entre pares de ítems equivalentes. Valores bajos son deseables. Un valor alto sugiere respuestas descuidadas o aleatorias; interprete el informe con cautela.' }) },
                        { key: 'impression_management', label: t('reports.review.impressionManagement', { defaultValue: 'Positive impression' }), s: data.integrity_scales.impression_management, hint: t('reports.review.impressionHint', { defaultValue: '% of trap items answered with the socially-desirable extreme' }), explain: t('reports.review.impressionExplain', { defaultValue: 'Mide la tendencia a elegir la respuesta socialmente deseable en ítems de control. Valores bajos son deseables. Un valor alto sugiere posible embellecimiento de la imagen; contraste con la entrevista.' }) },
                      ].map((it) => {
                        const score = it.s?.score ?? 50;
                        const code = it.s?.band?.code || 'medium';
                        const tone = code === 'high' ? '#dc2626' : code === 'low' ? '#16a34a' : '#ca8a04';
                        const isES = (i18n.language || '').startsWith('es');
                        const bandLabel = it.s?.band ? (isES ? (it.s.band.es || it.s.band.en) : (it.s.band.en || it.s.band.es)) : '—';
                        return (
                          <div key={it.key} className="rounded-lg border bg-card p-3" data-testid={`integrity-${it.key}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-semibold">{it.label}</span>
                              <Badge variant="outline" style={{ color: tone, borderColor: tone }}>{bandLabel}</Badge>
                            </div>
                            <div className="mt-2 h-2 rounded-full bg-muted/60 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${score}%`, background: tone }} />
                            </div>
                            <div className="flex items-center justify-between mt-1.5 text-xs">
                              <span className="text-muted-foreground">{it.hint}</span>
                              <span className="font-bold tabular-nums">{it.s?.available === false ? '—' : score}</span>
                            </div>
                            {it.s?.available === false && (
                              <p className="text-xs italic text-muted-foreground mt-1">{t('reports.review.integrityNotAvailable', { defaultValue: 'No tagged items in this evaluation — neutral default shown.' })}</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed border-t border-border/60 pt-1.5" data-testid={`integrity-${it.key}-explain`}>{it.explain}</p>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}

                {['praxora_p4', 'dimensional_profile'].includes(content.report_mode) ? (
                  <PraxoraP4Panel
                    p4={content.p4}
                    mode={content.report_mode}
                    editable={canSave}
                    instrumentName={data?.assignment?.title}
                    onNarrativeChange={(key, value) => setContent((prev) => ({
                      ...prev,
                      p4: { ...prev.p4, narrative: { ...(prev.p4?.narrative || {}), [key]: value } },
                    }))}
                  />
                ) : (
                <>
                <Card className="p-6 shadow-card space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.summary')}</Label>
                    <AutoGrowTextarea data-testid="field-summary" className="mt-1.5" minHeight={180} value={content.summary || ''} onChange={(e) => setContent({ ...content, summary: e.target.value })} />
                  </div>

                  <div className="grid md:grid-cols-3 gap-3 pt-3 border-t">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.suitabilityScore')}</Label>
                      <Input type="number" min={0} max={100} value={content.suitability?.score ?? 0} onChange={(e) => setContent({ ...content, suitability: { ...content.suitability, score: parseInt(e.target.value || '0', 10) } })} className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.verdict')}</Label>
                      <Select value={content.suitability?.verdict || 'borderline'} onValueChange={(v) => setContent({ ...content, suitability: { ...content.suitability, verdict: v } })}>
                        <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['strong_fit', 'good_fit', 'borderline', 'not_recommended'].map((v) => (
                            <SelectItem key={v} value={v}>{t(`reports.review.verdictLabel.${v}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.rationale')}</Label>
                      <AutoGrowTextarea className="mt-1.5" minHeight={140} value={content.suitability?.rationale || ''} onChange={(e) => setContent({ ...content, suitability: { ...content.suitability, rationale: e.target.value } })} />
                    </div>
                    <p className="text-xs text-muted-foreground/90 italic leading-relaxed md:col-span-3" data-testid="advisor-suitability-explainer">
                      {fit?.source === 'competency_profile'
                        ? t('reports.review.suitabilityExplainerTC')
                        : isTtReport
                          ? t('reports.review.suitabilityExplainerTT')
                          : t('reports.review.suitabilityExplainer', { defaultValue: 'El Puntaje de Idoneidad (0–100) expresa la solidez de la recomendación general para esta posición específica. Se calcula a partir del promedio ponderado de las secciones (según los pesos del cuestionario) y se ajusta con el criterio profesional del consultor; por eso una sección baja puede coexistir con un puntaje global alto.' })}
                    </p>
                  </div>
                </Card>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="p-6 shadow-card"><ListEditor t={t} label={t('reports.review.strengths')} testid="list-strengths" color="success" items={content.strengths} onChange={(v) => setContent({ ...content, strengths: v })} /></Card>
                  <Card className="p-6 shadow-card"><ListEditor t={t} label={t('reports.review.weaknesses')} testid="list-weaknesses" color="warning" items={content.weaknesses} onChange={(v) => setContent({ ...content, weaknesses: v })} /></Card>
                  <Card className="p-6 shadow-card"><ListEditor t={t} label={t('reports.review.risks')} testid="list-risks" color="destructive" items={content.risks} onChange={(v) => setContent({ ...content, risks: v })} /></Card>
                  <Card className="p-6 shadow-card"><ListEditor t={t} label={t('reports.review.behaviors')} testid="list-behaviors" color="info" items={content.behaviors} onChange={(v) => setContent({ ...content, behaviors: v })} /></Card>
                  <Card className="p-6 shadow-card"><ListEditor t={t} label={t('reports.review.recommendations')} testid="list-recommendations" color="primary" items={content.recommendations} onChange={(v) => setContent({ ...content, recommendations: v })} /></Card>
                  <Card className="p-6 shadow-card"><ListEditor t={t} label={t('reports.review.training')} testid="list-training" color="accent" items={content.training_suggestions} onChange={(v) => setContent({ ...content, training_suggestions: v })} /></Card>
                </div>
                </>
                )}

                <Card className="p-6 shadow-card border-2 border-dashed border-primary/20 bg-primary/[0.02]">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-primary">{t('reports.review.advisorNotes')}</Label>
                  <AutoGrowTextarea data-testid="field-consultant-notes" className="mt-2" minHeight={220} placeholder={t('reports.review.notesPh')} value={content.consultant_notes || ''} onChange={(e) => setContent({ ...content, consultant_notes: e.target.value })} />
                </Card>
              </TabsContent>

              <TabsContent value="versions" className="mt-4">
                <Card className="p-6 shadow-card space-y-3">
                  <h3 className="font-display font-bold">{t('reports.review.versionHistory')}</h3>
                  {versions.map((v, i) => (
                    <div key={v.id} data-testid={`version-row-${v.version_number}`} className={`p-3 rounded-lg border flex items-center gap-3 cursor-pointer transition-smooth ${i === activeVersionIdx ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                      onClick={() => { setActiveVersionIdx(i); setContent(v.content || EMPTY_CONTENT); }}>
                      <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${v.author_type === 'ai' ? 'bg-info/10 text-info' : 'bg-primary/10 text-primary'}`}>
                        {v.author_type === 'ai' ? <Bot className="h-4 w-4" /> : <UserIcon className="h-4 w-4" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">v{v.version_number} · <span className="text-muted-foreground">{v.author_type === 'ai' ? `${v.model_provider}/${v.model_name || 'n/a'}` : t('reports.review.authorHmEdit')}</span></p>
                        <p className="text-xs text-muted-foreground">{fmt(v.created_at)}</p>
                      </div>
                      {v.id === report.current_version_id && <Badge className="text-xs" variant="outline">{t('common.current')}</Badge>}
                    </div>
                  ))}
                </Card>
              </TabsContent>

              <TabsContent value="compare" className="mt-4">
                {versions.length < 2 ? (
                  <Card className="p-8 text-center text-sm text-muted-foreground">
                    {t('reports.review.compareNeed2', { n: versions.length })}
                  </Card>
                ) : <ComparePanel versions={versions} t={t} />}
              </TabsContent>
            </Tabs>
          </div>

          {/* RIGHT: fit + audit + role profile */}
          <div className="space-y-4">
            {fit && (
              <Card className="p-6 shadow-card" data-testid="role-fit-panel">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="h-4 w-4 text-primary" />
                  <h3 className="font-display font-bold">
                    {fit.source === 'competency_profile'
                      ? t('reports.review.idoneidadTitle', { defaultValue: 'Idoneidad — Perfil de Puesto' })
                      : t('reports.review.roleFit')}
                  </h3>
                </div>
                {fit.source === 'competency_profile' && (fit.profile_name || data?.competency_profile?.name) && (
                  <p className="text-xs text-muted-foreground mb-3" data-testid="fit-profile-name">
                    {t('reports.review.idoneidadProfile', { name: fit.profile_name || data?.competency_profile?.name, defaultValue: 'Comparado contra: {{name}}' })}
                  </p>
                )}
                <div className="flex items-baseline gap-3">
                  <p className="text-4xl font-display font-bold">{fit.fit_score}</p>
                  <Badge className="uppercase">{t(`reports.review.fitVerdictLabel.${fit.verdict}`, { defaultValue: FIT_VERDICT_LABEL[fit.verdict] })}</Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{fit.rationale}</p>
                <div className="mt-4 pt-4 border-t space-y-2">
                  {fit.gaps?.map((g) => {
                    const tone = GAP_STATUS_TONE[g.status] || GAP_STATUS_TONE.below;
                    return (
                      <div key={g.competency_code} className="flex items-center justify-between text-xs">
                        <span className="font-medium">{compName(g)}</span>
                        {g.status === 'not_evaluated' ? (
                          <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground italic">
                            {t('reports.review.notEvaluated', { defaultValue: 'No evaluada' })}
                          </span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} tabular-nums`}>{g.actual} / {g.expected} ({g.delta >= 0 ? '+' : ''}{g.delta})</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {fit.risks?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs uppercase tracking-wider font-semibold text-destructive flex items-center gap-1"><ShieldAlert className="h-3 w-3" /> {t('reports.review.criticalRisks')}</p>
                    <ul className="text-xs text-muted-foreground mt-1.5 space-y-1">
                      {fit.risks.map((r) => <li key={r}>· {t(`reports.competencies.${String(r).toLowerCase()}`, { defaultValue: r })}</li>)}
                    </ul>
                  </div>
                )}
              </Card>
            )}
            {!fit && isTcReport && (
              <Card className="p-6 shadow-card border-dashed" data-testid="fit-unavailable-panel">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldAlert className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-display font-bold">{t('reports.review.noFitTitle', { defaultValue: 'Idoneidad no disponible' })}</h3>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{t('reports.review.noFitBody', { defaultValue: 'Esta evaluación no tiene un Perfil de Puesto vinculado con niveles requeridos, por lo que la Idoneidad no puede calcularse todavía.' })}</p>
              </Card>
            )}

            <Card className="p-6 shadow-card" data-testid="audit-timeline">
              <div className="flex items-center gap-2 mb-3">
                <History className="h-4 w-4 text-primary" />
                <h3 className="font-display font-bold">{t('reports.review.auditTimeline')}</h3>
              </div>
              <div className="space-y-3">
                {auditLog.length === 0 && <p className="text-xs text-muted-foreground italic">{t('reports.review.noTransitions')}</p>}
                {auditLog.map((e, i) => (
                  <div key={i} className="text-xs border-l-2 border-border pl-3 -ml-px">
                    <p className="font-medium">{t(`reports.review.auditAction.${e.action}`, { defaultValue: e.action.replace(/_/g, ' ') })} <span className="text-muted-foreground font-normal">· {t(`reports.review.actorRole.${e.actor_role || 'system'}`, { defaultValue: e.actor_role || 'system' })}</span></p>
                    <p className="text-muted-foreground">
                      {e.previous_status ? t(`reports.review.statusLabel.${e.previous_status}`, { defaultValue: e.previous_status }) : '—'} → <span className="font-semibold">{e.new_status ? t(`reports.review.statusLabel.${e.new_status}`, { defaultValue: e.new_status }) : '—'}</span>
                    </p>
                    <p className="text-muted-foreground">{fmt(e.timestamp)}</p>
                    {e.human_review_confirmed && <p className="text-muted-foreground mt-0.5">{t('reports.review.humanReviewConfirmed')}</p>}
                    {e.ai_generated && <p className="text-muted-foreground mt-0.5">{t('reports.review.aiGeneratedDraft')}</p>}
                    {e.notes && <p className="text-muted-foreground italic mt-0.5">"{e.notes}"</p>}
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* R1 — HM Admin review decision dialog (return / complete) */}
      <Dialog open={!!reviewDialog} onOpenChange={(v) => !v && setReviewDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{reviewDialog === 'return' ? t('reports.review.returnWithComments') : t('reports.review.completeReview')}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              {t('reports.review.reviewCommentsLabel')}{reviewDialog === 'complete' ? ` (${t('reports.review.optionalSuffix')})` : ''}
            </Label>
            <Textarea data-testid="review-comments-input" className="mt-2" value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} placeholder={t('reports.review.reviewCommentsPh')} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReviewDialog(null)}>{t('reports.review.cancel')}</Button>
            <Button data-testid="review-decision-confirm" onClick={onReviewDecision} disabled={busy} className={reviewDialog === 'complete' ? 'bg-success hover:bg-success/90' : ''}>
              {reviewDialog === 'return' ? t('reports.review.returnWithComments') : t('reports.review.completeReview')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R1 — Advisor archive request dialog */}
      <Dialog open={archiveReqOpen} onOpenChange={setArchiveReqOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('reports.review.requestArchiveTitle')}</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.archiveReasonLabel')}</Label>
              <Select value={archiveReason} onValueChange={setArchiveReason}>
                <SelectTrigger className="mt-2" data-testid="archive-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['duplicate', 'client_cancelled', 'created_by_mistake', 'other'].map((c) => (
                    <SelectItem key={c} value={c}>{t(`reports.review.archiveReason.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea data-testid="archive-reason-text" value={archiveReasonText} onChange={(e) => setArchiveReasonText(e.target.value)} placeholder={t('reports.review.archiveReasonTextPh')} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setArchiveReqOpen(false)}>{t('reports.review.cancel')}</Button>
            <Button data-testid="archive-request-confirm" onClick={onRequestArchive} disabled={busy}>{t('reports.review.requestArchive')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R1 — Discard confirmation */}
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('reports.review.discardTitle')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('reports.review.discardBody')}</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDiscardOpen(false)}>{t('reports.review.cancel')}</Button>
            <Button data-testid="discard-confirm" variant="destructive" onClick={onDiscard} disabled={busy}>{t('reports.review.discardConfirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('reports.review.rejectReport')}</DialogTitle></DialogHeader>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.reason')}</Label>
            <Textarea data-testid="reject-reason-input" className="mt-2" placeholder={t('reports.review.rejectReasonPh')} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>{t('reports.review.cancel')}</Button>
            <Button data-testid="reject-confirm" variant="destructive" onClick={onReject}>{t('reports.review.rejectReport')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate dialog */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('reports.review.regenAiDraft')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">{t('reports.review.regenSubtitle')}</p>
          <div className="py-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.optionalHint')}</Label>
            <Textarea data-testid="regen-hint-input" className="mt-2" placeholder={t('reports.review.regenHintPh')} value={regenHint} onChange={(e) => setRegenHint(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRegenOpen(false)}>{t('reports.review.cancel')}</Button>
            <Button data-testid="regen-confirm" onClick={onRegen} className="bg-gradient-primary">{t('reports.review.generate')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{report?.status === 'released_to_client' ? t('reports.review.resendReport') : t('reports.review.sendByEmail')}</DialogTitle></DialogHeader>
          {report?.status === 'released_to_client' && (
            <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs" data-testid="resend-warning">
              {t('reports.review.resendWarning')}
            </div>
          )}
          <p className="text-sm text-muted-foreground -mt-2">
            {t('reports.review.sendBodyDesc')}
          </p>
          <div className="py-2 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('reports.review.recipients')}</Label>
              <Textarea
                data-testid="send-recipients-input"
                className="mt-2"
                placeholder={t('reports.review.recipientsPh')}
                value={sendEmails}
                onChange={(e) => setSendEmails(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">{t('reports.review.recipientsHint')}</p>
            </div>
            <label className="flex items-start gap-2 text-sm select-none cursor-pointer" data-testid="send-include-evaluated">
              <input
                type="checkbox"
                checked={sendIncludeEval}
                onChange={(e) => setSendIncludeEval(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-border"
              />
              <span>
                {t('reports.review.alsoSendToEval')}{candidate?.email ? <> (<span className="font-mono text-xs">{candidate.email}</span>)</> : ''}
              </span>
            </label>
            {sendLog && sendLog.length > 0 && (
              <div className="border border-border rounded-md p-3 bg-muted/30 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t('reports.review.previousSends')}</p>
                {sendLog.slice(0, 3).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs" data-testid={`send-log-${r.id}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${r.status === 'sent' ? 'bg-success' : 'bg-destructive'}`} />
                    <span className="font-medium">{r.status}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{fmt(r.sent_at || r.created_at)}</span>
                    <span className="text-muted-foreground">·</span>
                    <span className="truncate">{(r.recipients || []).join(', ')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSendOpen(false)} disabled={sending}>{t('reports.review.cancel')}</Button>
            <Button
              data-testid="send-confirm"
              onClick={onSend}
              disabled={sending}
              className="gap-1.5 bg-gradient-primary"
            >
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> {t('reports.review.sending')}</> : <><Send className="h-4 w-4" /> {t('reports.review.send')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

